import { get } from 'lodash'

export function markCheckbox(question, option) {
  if (Array.isArray(question.answer)) {
    if (question.answer.includes(option)) {
      question.answer = question.answer.filter((item) => item !== option)
    } else {
      question.answer.push(option)
    }
  } else {
    question.answer = [option]
  }
}

export function formatDateToSQLInChicagoTime(date) {
  const options = {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }

  const chicagoTime = date.toLocaleString('en-US', options)

  // The result of localString method looks like: "MM/DD/YYYY, HH:MM:SS"
  const [datePart, timePart] = chicagoTime.split(', ')
  const [month, day, year] = datePart.split('/')

  return `${year}-${month}-${day} ${timePart}`
}

export function stringMapValues(template = '', params = {}) {
  return template.replace(/\$\{(\w+)\}/g, (match, key) => {
    if (Object.prototype.hasOwnProperty.call(params, key)) {
      return get(params, key, match)
    } else {
      return match // Return original placeholder if key doesn't exists
    }
  })
}

export const processDefinition = (definition, id) => {
  const title = definition.title || 'No title'
  let contentTitle = ''
  let processedText = ''

  if (!definition.body || typeof definition.body !== 'string') {
    return null
  }

  processedText = definition.body.replace(
    /<definition([^>]*)>([\s\S]*?)<\/definition>/g,
    (match, attributes, content) => {
      contentTitle = content
      const idMatch = attributes.match(/id="([^"]*)"/)
      const id = idMatch ? idMatch[1] : ''
      return `<span class="words-for-definition" id="${id}" data-definition-id="${id}">${content.trim()}</span>`
    }
  )

  processedText = processedText.replace(/<(?!\/?strong\b|\/?span\b)[^>]*>/g, '')

  processedText = processedText.replace(
    /(https?:\/\/[^\s\n]+)/g,
    '<a href="$1" target="_blank">$1</a>'
  )

  return {
    title,
    processedText,
    contentTitle,
    id
  }
}

export function removeHTMLTags(str = '') {
  return str.replace(/<\/?[^>]+(>|$)/g, '')
}

export const isValidHexColor = (color) => {
  const hexRegex = /^#?([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/
  return hexRegex.test(color)
}

/**
 * Converts a date string to a readable format like "Aug 17, 2023".
 * Supports both YYYY-MM-DD and ISO 8601 formats.
 * @param {string} dateString - Date string in YYYY-MM-DD or ISO 8601 format
 * @returns {string} Formatted date string (e.g. "Aug 17, 2023")
 */
export function convertDate(dateString) {
  if (!dateString) return ''

  // Handle ISO 8601 format (contains 'T')
  if (dateString.includes('T')) {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  // Handle YYYY-MM-DD format
  const [year, month, day] = dateString.split('-')
  const date = new Date(year, month - 1, day)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export function getTodayFormatted() {
  const today = new Date()
  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

/**
 * Checks whether a date string (ISO 8601 or YYYY-MM-DD) represents today.
 * @param {string} dateString
 * @returns {boolean}
 */
export function isToday(dateString) {
  if (!dateString) return false
  const todayStr = getTodayFormatted()
  return dateString === todayStr || (dateString.includes('T') && dateString.startsWith(todayStr))
}

/**
 * Truncate text to a maximum length, appending '...' if exceeded.
 * @param {string} text - The text to truncate
 * @param {number} maxLength - Maximum character length (default 80)
 * @returns {string} Truncated text or '-' if empty
 */
export function truncateText(text, maxLength = 80) {
  if (!text) return '-'
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text
}

export const ACTIVITY_NEWEST_TO_OLDEST = 1
export const ACTIVITY_OLDEST_TO_NEWEST = 2
export const ORDER_A_TO_Z = 3
export const ORDER_Z_TO_A = 4
export const ORDER_NEWEST_TO_OLDEST = 5
export const ORDER_OLDEST_TO_NEWEST = 6

/**
 * Formats a date from YYYY-MM-DD to MM/DD/YYYY format
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {string} Date string in MM/DD/YYYY format
 */
export function formatDateMMDDYYYY(dateStr) {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  return `${month}/${day}/${year}`;
}

/**
 * Sanitizes a date string for filenames by replacing slashes with dashes.
 * Returns an empty string if input is not a valid string.
 */
export function sanitizeDateForFilename(dateStr) {
  if (typeof dateStr !== 'string' || !dateStr) return ''
  return dateStr.replace(/\//g, '-')
}

/**
 * Calculates the Group Insurance Type ID (0-3) based on the number of employees
 * on the plan (question 9) and the funding type (question 11).
 * @param {number|object} noEmployees - Employees on plan or object containing value
 * @param {number|object} fundingType - 0 = Fully-insured, 1 = Self-insured or object containing value
 * @param {number} largeGroupCutoff - Minimum employees to be considered a large group (default 50)
 * @returns {(number|null)} ID 0-3 or null when inputs are invalid/missing
 */
export function calculateGroupInsuranceTypeId(noEmployees, fundingType, largeGroupCutoff = 50) {
  // Accept objects like { value: X }
  const norm = v => (typeof v === 'object' && v !== null ? (v.value ?? v) : v)
  const employees = norm(noEmployees)
  const fund = norm(fundingType)

  if (employees === null || employees === undefined || employees === '' ||
      fund === null || fund === undefined || fund === '') {
    return null
  }

  const isFullyInsured = fund === 0 // 0 → Fully-Insured (FI)
  const isLargeGroup = employees >= largeGroupCutoff

  // SECTION_TYPES
  // 0: 'LARGE GROUPS'
  // 1: 'SMALL GROUP - SELF INSURED'
  // 2: 'LARGE GROUP - SELF INSURED'
  // 3: 'SMALL GROUP'

  if (!isLargeGroup && isFullyInsured) return 3
  if (isLargeGroup  && isFullyInsured) return 0
  if (!isLargeGroup && !isFullyInsured) return 1
  if (isLargeGroup  && !isFullyInsured) return 2

  return null
}
